export class GoogleDriveService {

  /**
   * Extract file ID from a Google Drive share link
   * Supports various Google Drive share link formats
   */
  static extractFileIdFromShareLink(shareLink: string): string | null {
    try {
      console.log('Extracting file ID from link:', shareLink);
      
      // Handle different Google Drive share link formats:
      // https://drive.google.com/file/d/{FILE_ID}/view?usp=sharing
      // https://drive.google.com/file/d/{FILE_ID}/edit?usp=sharing
      // https://drive.google.com/open?id={FILE_ID}
      // https://docs.google.com/document/d/{FILE_ID}/edit
      // https://docs.google.com/spreadsheets/d/{FILE_ID}/edit
      // https://docs.google.com/presentation/d/{FILE_ID}/edit
      
      const patterns = [
        /\/file\/d\/([a-zA-Z0-9-_]+)/,
        /\/document\/d\/([a-zA-Z0-9-_]+)/,
        /\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/,
        /\/presentation\/d\/([a-zA-Z0-9-_]+)/,
        /[?&]id=([a-zA-Z0-9-_]+)/,
        /\/d\/([a-zA-Z0-9-_]+)/, // More generic pattern
      ];

      for (const pattern of patterns) {
        const match = shareLink.match(pattern);
        if (match) {
          console.log('File ID extracted:', match[1]);
          return match[1];
        }
      }

      console.warn('No file ID pattern matched for link:', shareLink);
      return null;
    } catch (error) {
      console.error('Error extracting file ID from share link:', error);
      return null;
    }
  }

  /**
   * Fetch file content directly from Google Drive share URL without API
   * This method works for files that are shared publicly
   */
  static async fetchFileContentDirectly(shareUrl: string): Promise<string> {
    try {
      console.log('Fetching file content directly from share URL:', shareUrl);
      
      const fileId = this.extractFileIdFromShareLink(shareUrl);
      if (!fileId) {
        throw new Error('Không thể trích xuất file ID từ link chia sẻ');
      }

      // Try different direct access methods based on file type detection
      const downloadStrategies = [
        // Strategy 1: Direct download for regular files
        {
          url: `https://drive.google.com/uc?export=download&id=${fileId}`,
          description: 'Direct download'
        },
        
        // Strategy 2: Google Docs text export
        {
          url: `https://docs.google.com/document/d/${fileId}/export?format=txt`,
          description: 'Google Docs text export'
        },
        
        // Strategy 3: Google Sheets CSV export
        {
          url: `https://docs.google.com/spreadsheets/d/${fileId}/export?format=csv`,
          description: 'Google Sheets CSV export'
        },
        
        // Strategy 4: Google Docs HTML export (backup)
        {
          url: `https://docs.google.com/document/d/${fileId}/export?format=html`,
          description: 'Google Docs HTML export'
        },
        
        // Strategy 5: Alternative direct download
        {
          url: `https://drive.google.com/file/d/${fileId}/view`,
          description: 'View page content extraction',
          extractContent: true
        }
      ];

      let content = '';
      let lastError: Error | null = null;

      for (const strategy of downloadStrategies) {
        try {
          console.log(`Trying strategy: ${strategy.description} - ${strategy.url}`);
          
          const response = await fetch(strategy.url, {
            method: 'GET',
            headers: {
              'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36',
              'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,*/*;q=0.8',
              'Accept-Language': 'en-US,en;q=0.5',
              'Accept-Encoding': 'gzip, deflate, br',
              'DNT': '1',
              'Connection': 'keep-alive',
              'Upgrade-Insecure-Requests': '1',
            },
          });

          console.log(`Response status: ${response.status} for strategy: ${strategy.description}`);

          if (response.ok) {
            const text = await response.text();
            
            // Special handling for view page content extraction
            if (strategy.extractContent) {
              // Try to extract content from Google Drive view page
              const extractedContent = this.extractContentFromViewPage(text);
              if (extractedContent && extractedContent.length > 50) {
                console.log(`Successfully extracted content from view page, length: ${extractedContent.length}`);
                content = extractedContent;
                break;
              }
            } else {
              // Regular content validation
              if (text && text.length > 50 && 
                  !text.includes('Google Drive - Virus scan warning') &&
                  !text.includes('Sorry, the file you have requested does not exist') &&
                  !text.includes('Access denied')) {
                console.log(`Successfully fetched content using ${strategy.description}, length: ${text.length}`);
                content = text;
                break;
              } else {
                console.log(`Response seems to be error page or too short for strategy: ${strategy.description}`);
              }
            }
          } else {
            console.log(`HTTP error ${response.status} for strategy: ${strategy.description}`);
          }
        } catch (error) {
          console.log(`Failed with strategy ${strategy.description}:`, (error as Error).message);
          lastError = error as Error;
        }
      }

      if (!content) {
        throw new Error(`Không thể fetch file content trực tiếp. Tất cả các phương pháp đều thất bại. File có thể không được share public hoặc cần quyền truy cập đặc biệt. Last error: ${lastError?.message || 'Unknown error'}`);
      }

      return content;
    } catch (error) {
      console.error('Error fetching file content directly:', error);
      throw error;
    }
  }

  /**
   * Extract content from Google Drive view page HTML
   * This is a fallback method for when direct download fails
   */
  private static extractContentFromViewPage(html: string): string | null {
    try {
      // Try to find content in various HTML patterns that Google Drive uses
      const patterns = [
        // Pattern 1: Look for content in script tags
        /"docs-text-content[^"]*"[^>]*>([^<]+)</g,
        
        // Pattern 2: Look for content in data attributes
        /data-content="([^"]+)"/g,
        
        // Pattern 3: Look for plain text content
        /<pre[^>]*>([^<]+)<\/pre>/g,
        
        // Pattern 4: Look for content in divs with specific classes
        /<div[^>]*class="[^"]*content[^"]*"[^>]*>([^<]+)<\/div>/g
      ];

      for (const pattern of patterns) {
        const matches = html.matchAll(pattern);
        for (const match of matches) {
          if (match[1] && match[1].trim().length > 50) {
            return match[1].trim();
          }
        }
      }

      return null;
    } catch (error) {
      console.error('Error extracting content from view page:', error);
      return null;
    }
  }

  /**
   * Get file content from a Google Drive share link
   * Uses direct fetch method only (no authentication required)
   */
  static async getFileContentFromShareLink(shareLink: string): Promise<string> {
    try {
      console.log('Processing share link:', shareLink);
      
      const fileId = this.extractFileIdFromShareLink(shareLink);
      console.log('Extracted file ID:', fileId);
      
      if (!fileId) {
        throw new Error('Không thể trích xuất file ID từ link chia sẻ. Đảm bảo link có định dạng Google Drive hợp lệ.');
      }

      // Only use direct fetch method
      console.log('Using direct fetch method...');
      const content = await this.fetchFileContentDirectly(shareLink);
      console.log('Direct fetch successful!');
      return content;
    } catch (error) {
      console.error('Error getting file content from share link:', error);
      throw new Error(`Không thể đọc nội dung từ link chia sẻ: ${(error as Error).message}`);
    }
  }

  /**
   * Alias method for extractFileIdFromShareLink for backward compatibility
   */
  static extractFileIdFromUrl(url: string): string | null {
    return this.extractFileIdFromShareLink(url);
  }

}
